import { forwardRef, type ReactNode } from 'react';
import { motion, type HTMLMotionProps } from 'motion/react';
import { spring } from '../motion/presets';
import { cx } from '../internal/cx';

export type CardProps = Omit<HTMLMotionProps<'div'>, 'children'> & {
  variant?: 'default' | 'raised' | 'outline';
  /** Carte cliquable : lift au hover (y:-4 + shadow-pop + léger tilt). */
  interactive?: boolean;
  children?: ReactNode;
};

const VARIANTS: Record<NonNullable<CardProps['variant']>, string> = {
  default: 'bg-surface border border-ink-600/60 shadow-card',
  raised: 'bg-ink-700 border border-ink-600/60 shadow-pop',
  outline: 'bg-transparent border border-ink-600',
};

/**
 * Carte du design system (rayon xl, surface thème-aware).
 * `interactive` → lift + tilt au hover (désactivé si reduced-motion via MotionConfig).
 */
export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { variant = 'default', interactive = false, className, children, ...rest },
  ref,
) {
  return (
    <motion.div
      ref={ref}
      whileHover={interactive ? { y: -4, rotate: -0.8 } : undefined}
      whileTap={interactive ? { scale: 0.99 } : undefined}
      transition={spring.soft}
      className={cx(
        'rounded-xl p-5',
        VARIANTS[variant],
        interactive &&
          'cursor-pointer transition-shadow duration-fast ease-fun hover:shadow-pop',
        className,
      )}
      {...rest}
    >
      {children}
    </motion.div>
  );
});
